import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Search, Edit, Trash2, Info, Eye, LayoutGrid, List, Upload, X, QrCode, RefreshCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { SearchableOwnerSelect } from '@/components/SearchableOwnerSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { compressImage, validateImageFile } from '@/utils/imageCompression';
import { uploadClinicImage } from '@/utils/cloudinary';

export const Pets = () => {
  const navigate = useNavigate();
  const { subscription, plan, getPetsPerOwnerLimit, canAddPetForOwner: subCanAddPetForOwner } = useSubscription();
  const [pets, setPets] = useState([]);
  const [owners, setOwners] = useState([]);
  const [petTypes, setPetTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('card');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingPetId, setDeletingPetId] = useState(null);
  const [editingPet, setEditingPet] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadToken, setUploadToken] = useState(null);
  const [showQrPanel, setShowQrPanel] = useState(false);
  const [isCheckingQr, setIsCheckingQr] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    pet_type_id: '',
    owner_id: '',
    image_url: null,
    breed: '',
    gender: '',
    weight: '',
    color: '',
  });

  useEffect(() => {
    fetchPets();
    fetchOwners();
    fetchPetTypes();
  }, []);

  const fetchPets = async () => {
    try {
      const response = await api.get('/pets');
      setPets(response.data);
    } catch (error) {
      toast.error('Failed to load pets');
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await api.get('/owners');
      setOwners(response.data);
    } catch (error) {
      toast.error('Failed to load owners');
    }
  };

  const fetchPetTypes = async () => {
    try {
      const response = await api.get('/pet-types');
      setPetTypes(response.data);
    } catch (error) {
      toast.error('Failed to load pet types');
    }
  };

  const getOwnerPetCount = (ownerId) => {
    return pets.filter(pet => pet.owner_id === ownerId).length;
  };

  const canAddPetForOwner = (ownerId) => {
    if (!ownerId || editingPet) return true; // Allow if editing or no owner selected
    return subCanAddPetForOwner(getOwnerPetCount(ownerId));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file, 3);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    try {
      toast.info('Compressing image...');
      const compressedFile = await compressImage(file, {
        maxSizeMB: 3,
        maxWidthOrHeight: 1920,
        quality: 0.8
      });

      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(compressedFile);

      setImageFile(compressedFile);
      toast.success(`Image compressed to ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB`);
    } catch (error) {
      toast.error('Failed to process image: ' + error.message);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData({ ...formData, image_url: null });
    setShowQrPanel(false);
    setUploadToken(null);
  };

  const generateUploadQr = async () => {
    try {
      const response = await api.post('/upload-sessions');
      setUploadToken(response.data.token);
      setShowQrPanel(true);
    } catch (error) {
      toast.error('Failed to generate QR code. Please try again.');
    }
  };

  const checkForImage = async () => {
    if (!uploadToken) return;
    setIsCheckingQr(true);
    try {
      const response = await api.get(`/upload-sessions/${uploadToken}`);
      if (response.data.image_url) {
        setFormData((prev) => ({ ...prev, image_url: response.data.image_url }));
        setImagePreview(response.data.image_url);
        setShowQrPanel(false);
        setUploadToken(null);
        toast.success('Photo received from mobile!');
      } else {
        toast.info('No photo yet — complete the upload on your phone first.');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 404 || detail.toLowerCase().includes('expired')) {
        toast.error('QR code expired. Please generate a new one.');
        setShowQrPanel(false);
        setUploadToken(null);
      } else {
        toast.error('Failed to check for image. Please try again.');
      }
    } finally {
      setIsCheckingQr(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check limit before submitting for new pets
    if (!editingPet && !canAddPetForOwner(formData.owner_id)) {
      const limit = getPetsPerOwnerLimit();
      toast.error(`This owner has reached the limit of ${limit} pets (${plan} plan). Please upgrade your subscription.`);
      return;
    }

    setUploading(true);
    try {
      let imageUrl = formData.image_url;

      if (imageFile) {
        toast.info('Uploading image...');
        imageUrl = await uploadClinicImage(imageFile, 'pets');
        toast.success('Image uploaded successfully');
      }

      const payload = {
        ...formData,
        image_url: imageUrl || null,
        weight: formData.weight !== '' ? parseFloat(formData.weight) : null,
        breed: formData.breed || null,
        gender: formData.gender || null,
        color: formData.color || null,
      };

      if (editingPet) {
        await api.put(`/pets/${editingPet.id}`, payload);
        toast.success('Pet updated successfully');
      } else {
        await api.post('/pets', payload);
        toast.success('Pet created successfully');
      }
      setIsDialogOpen(false);
      setFormData({ name: '', date_of_birth: '', pet_type_id: '', owner_id: '', image_url: null, breed: '', gender: '', weight: '', color: '' });
      setEditingPet(null);
      setImageFile(null);
      setImagePreview(null);
      fetchPets();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Operation failed');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (pet) => {
    setEditingPet(pet);
    setFormData({
      name: pet.name,
      date_of_birth: pet.date_of_birth,
      pet_type_id: pet.pet_type_id,
      owner_id: pet.owner_id,
      image_url: pet.image_url,
      breed: pet.breed || '',
      gender: pet.gender || '',
      weight: pet.weight || '',
      color: pet.color || '',
    });
    setImageFile(null);
    setImagePreview(pet.image_url || null);
    setIsDialogOpen(true);
  };

   const handleDelete = (id) => {
      setDeletingPetId(id);
      setIsDeleteDialogOpen(true);
    };
  
  const confirmDelete = async () => {
    try {
      await api.delete(`/pets/${deletingPetId}`);
      toast.success('Pet deleted successfully');
      fetchPets();
    } catch (error) {
      toast.error('Failed to delete pet');
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingPetId(null);
    }
  };

  const getOwnerName = (ownerId) => {
    const owner = owners.find((o) => o.id === ownerId);
    return owner ? owner.full_name : 'Unknown';
  };

  const getPetTypeName = (petTypeId) => {
    const petType = petTypes.find((pt) => pt.id === petTypeId);
    return petType ? petType.name : 'Unknown';
  };

  const filteredPets = pets.filter((pet) =>
    pet.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 md:p-12" data-testid="pets-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Pets</h1>
          <p className="text-gray-600">Manage pets and their information</p>
        </div>
        <div className="flex gap-3">
          <div className="flex bg-white rounded-full shadow border border-stone-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('card')}
              className={`rounded-full ${viewMode === 'card' ? 'bg-sage-500 text-white hover:bg-sage-600 hover:text-white' : ''}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('table')}
              className={`rounded-full ${viewMode === 'table' ? 'bg-sage-500 text-white hover:bg-sage-600 hover:text-white' : ''}`}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setShowQrPanel(false);
              setUploadToken(null);
            }
          }}>
          <DialogTrigger asChild>
            <Button
              className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-lg"
              onClick={() => {
                setEditingPet(null);
                setFormData({ name: '', date_of_birth: '', pet_type_id: '', owner_id: '', image_url: null, breed: '', gender: '', weight: '', color: '' });
                setImageFile(null);
                setImagePreview(null);
              }}
              data-testid="add-pet-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Pet
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPet ? 'Edit Pet' : 'Add New Pet'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="pet-form">
              <div>
                <Label htmlFor="owner_id">Owner</Label>
                <SearchableOwnerSelect
                  owners={owners.map((owner) => {
                    const petCount = getOwnerPetCount(owner.id);
                    const limit = getPetsPerOwnerLimit();
                    return {
                      ...owner,
                      full_name: `${owner.full_name} (${petCount}/${limit} pets)`
                    };
                  })}
                  value={formData.owner_id}
                  onValueChange={(value) => setFormData({ ...formData, owner_id: value })}
                  placeholder="Select owner"
                  testId="pet-owner-select"
                  className="mt-2"
                  disabled={false}
                />
                {!editingPet && formData.owner_id && !canAddPetForOwner(formData.owner_id) && (
                  <p className="text-sm text-red-600 mt-1">
                    This owner has reached the pet limit. Please upgrade to add more pets.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="name">Pet Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="pet-name-input"
                />
              </div>
              <div>
                <Label htmlFor="pet_type_id">Pet Type</Label>
                <Select value={formData.pet_type_id} onValueChange={(value) => setFormData({ ...formData, pet_type_id: value })}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="pet-type-select">
                    <SelectValue placeholder="Select pet type" />
                  </SelectTrigger>
                  <SelectContent>
                    {petTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="pet-dob-input"
                />
              </div>
              <div>
                <Label htmlFor="breed">Breed (optional)</Label>
                <Input
                  id="breed"
                  value={formData.breed}
                  onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                  className="rounded-xl mt-2"
                  placeholder="e.g. Labrador Retriever"
                />
              </div>
              <div>
                <Label htmlFor="gender">Gender</Label>
                <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                  <SelectTrigger className="rounded-xl mt-2">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="weight">Weight (kg, optional)</Label>
                  <Input
                    id="weight"
                    type="number"
                    min="0"
                    step="0.1"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    className="rounded-xl mt-2"
                    placeholder="e.g. 5.2"
                  />
                </div>
                <div>
                  <Label htmlFor="color">Color (optional)</Label>
                  <Input
                    id="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="rounded-xl mt-2"
                    placeholder="e.g. Golden"
                  />
                </div>
              </div>
              <div>
                <Label>Photo (optional)</Label>
                <p className="text-xs text-gray-500 mt-1 mb-2">Max 3MB. Image will be automatically compressed.</p>
                <div className="space-y-3">
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-xl border-2 border-sage-200"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-sage-400 transition-colors">
                      <input
                        id="pet-image"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleImageChange}
                        className="hidden"
                        data-testid="pet-image-input"
                      />
                      <label htmlFor="pet-image" className="cursor-pointer">
                        <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm font-medium text-gray-700">Click to upload photo</p>
                        <p className="text-xs text-gray-500 mt-1">JPG, PNG or WebP (max 3MB)</p>
                      </label>
                    </div>
                  )}
                </div>
              </div>
              {!imagePreview && (
                <div>
                  {!showQrPanel ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full rounded-xl border-dashed border-2 border-sage-300 text-sage-700 hover:bg-sage-50"
                      onClick={generateUploadQr}
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Scan QR Code to upload from phone
                    </Button>
                  ) : (
                    <div className="border rounded-2xl p-4 bg-sage-50 border-sage-200 text-center space-y-3">
                      <p className="text-sm font-semibold text-sage-800">Scan with your phone</p>
                      <div className="flex justify-center">
                        <QRCodeSVG
                          value={`${window.location.origin}/pet-upload/${uploadToken}`}
                          size={180}
                          bgColor="#f7faf7"
                          fgColor="#2d5a2d"
                        />
                      </div>
                      <p className="text-xs text-gray-400 break-all">
                        {`${window.location.origin}/pet-upload/${uploadToken}`}
                      </p>
                      <p className="text-xs text-gray-400">Link expires in 15 minutes</p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1 rounded-full text-sm"
                          onClick={() => { setShowQrPanel(false); setUploadToken(null); }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          className="flex-1 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm"
                          onClick={checkForImage}
                          disabled={isCheckingQr}
                        >
                          <RefreshCw className={`w-4 h-4 mr-1 ${isCheckingQr ? 'animate-spin' : ''}`} />
                          {isCheckingQr ? 'Checking...' : 'Check for Image'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="pet-submit-button" disabled={uploading}>
                {uploading ? 'Uploading...' : editingPet ? 'Update Pet' : 'Create Pet'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {subscription && (
        <Alert className="mb-6 bg-sage-50 border-sage-200">
          <Info className="h-4 w-4 text-sage-600" />
          <AlertDescription className="text-sage-800">
            <span className="font-semibold capitalize">{subscription.plan_type} Plan:</span> You can add up to {getPetsPerOwnerLimit()} pets per owner.
            {subscription.plan_type === 'free' && (
              <span className="ml-1">
                Upgrade to Basic for 5 pets per owner.
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            placeholder="Search pets by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 rounded-xl"
            data-testid="pets-search-input"
          />
        </div>
      </div>

      {filteredPets.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-3xl shadow-lg">
          No pets found. Add your first pet to get started!
        </div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {filteredPets.map((pet, idx) => (
            <div
              key={pet.id}
              className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-105"
              data-testid={`pet-card-${idx}`}
            >
              <div className="h-36 md:h-48 bg-sage-100 flex items-center justify-center overflow-hidden">
                {pet.image_url ? (
                  <img src={pet.image_url} alt={pet.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-6xl">🐾</div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-heading font-bold text-sage-800 mb-2">{pet.name}</h3>
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-semibold">Type:</span> {getPetTypeName(pet.pet_type_id)}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-semibold">Owner:</span> {getOwnerName(pet.owner_id)}
                </p>
                {/* <p className="text-sm text-gray-600 mb-1">
                  <span className="font-semibold">DOB:</span> {pet.date_of_birth}
                </p>
                {pet.breed && (
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-semibold">Breed:</span> {pet.breed}
                  </p>
                )}
                {pet.gender && (
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-semibold">Gender:</span> {pet.gender}
                  </p>
                )}
                {(pet.weight || pet.color) && (
                  <p className="text-sm text-gray-600 mb-4">
                    {pet.weight && <span><span className="font-semibold">Weight:</span> {pet.weight} kg</span>}
                    {pet.weight && pet.color && <span className="mx-2">·</span>}
                    {pet.color && <span><span className="font-semibold">Color:</span> {pet.color}</span>}
                  </p>
                )} */}
                <div className="flex gap-2 mb-2">
                  <Button
                    size="sm"
                    className="flex-1 rounded-full bg-sage-100 hover:bg-sage-500 text-black hover:text-white transition-colors"
                    onClick={() => navigate(`/dashboard/pets/${pet.id}`)}
                    data-testid={`view-pet-${idx}`}
                  >
                    <Eye className="w-4 h-4 lg:mr-1" /><span className="hidden lg:inline">View</span>
                  </Button>
                </div>
                {/* <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(pet)}
                    className="flex-1 rounded-full"
                    data-testid={`edit-pet-${idx}`}
                  >
                    <Edit className="w-4 h-4 lg:mr-1" /><span className="hidden lg:inline">Edit</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(pet.id)}
                    className="flex-1 rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-pet-${idx}`}
                  >
                    <Trash2 className="w-4 h-4 lg:mr-1" /><span className="hidden lg:inline">Delete</span>
                  </Button>
                </div> */}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold text-sage-800">Name</TableHead>
                <TableHead className="font-bold text-sage-800">Type</TableHead>
                <TableHead className="font-bold text-sage-800">Owner</TableHead>
                <TableHead className="font-bold text-sage-800">Date of Birth</TableHead>
                <TableHead className="font-bold text-sage-800">Breed</TableHead>
                <TableHead className="font-bold text-sage-800">Gender</TableHead>
                <TableHead className="font-bold text-sage-800 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPets.map((pet, idx) => (
                <TableRow key={pet.id} data-testid={`pet-row-${idx}`}>
                  <TableCell className="font-medium">{pet.name}</TableCell>
                  <TableCell>{getPetTypeName(pet.pet_type_id)}</TableCell>
                  <TableCell>{getOwnerName(pet.owner_id)}</TableCell>
                  <TableCell>{pet.date_of_birth}</TableCell>
                  <TableCell>{pet.breed || '—'}</TableCell>
                  <TableCell>{pet.gender || '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/dashboard/pets/${pet.id}`)}
                        className="rounded-full"
                        data-testid={`view-pet-${idx}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(pet)}
                        className="rounded-full"
                        data-testid={`edit-pet-${idx}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(pet.id)}
                        className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-pet-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the pet and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
